/**
 * DashboardPage — Launcher principal après login.
 * ============================================================================
 * Refonte R4 (mai 2026) : alignement 100% sur le mockup validé
 *   mockup_launcher_6_tuiles_collectivite_MODERNE.html
 *
 * Refonte L13.5 (21 mai 2026) — Dashboards administré différenciés :
 *   Le DashboardPage est désormais un router qui rend une vue dédiée selon
 *   user.role :
 *     - 'administre' → <DashboardAdministre /> : 5 tuiles spécifiques citoyen
 *       (Mes signalements, Mes alertes, Mon association, Scolaire & cantine,
 *       Mes demandes urbanisme). Pas de Vue consolidée, pas de tuile Admin,
 *       pas de tuile Plateforme, pas de bandeau "Phase 1 active".
 *     - autres rôles (super_admin / admin / manager / agent) →
 *       <DashboardPro /> : grid 3×2 modules mairie + Vue consolidée +
 *       tuile Administration (conditionnelle) + tuile Plateforme
 *       (conditionnelle) + bandeau "Phase 1 active" (vue interne équipe).
 *
 *   Aucune régression côté pros : leur vue reste strictement identique
 *   à la R4 d'origine. Côté administré, les 6 tuiles génériques sont
 *   remplacées par les 5 tuiles citoyennes pertinentes.
 *
 * Structure conservée :
 *   - Topbar dashboard (glass + halo tech + LogoMark Landmark + nom mairie
 *     + Pill notifications + user-pill avatar + actions account/2fa/logout)
 *   - Hero : eyebrow date + grand titre "Bonjour {prénom}"
 *   - Auth via useAuth, métriques dynamiques, navigation par rôle
 *   - Tuiles Administration / Plateforme rendues conditionnellement (pros)
 * ============================================================================
 */

import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Badge, Card, LogoMark, Avatar, SectionEyebrow } from '@/components/ui';
import { PhoneLink } from '@/components/ui/PhoneLink';  // L13.9 fix Z5 (modale interne tel: desktop)
import { useAuth } from '@/lib/auth-context';
import { useModules } from '@/lib/modules-context';
import { api } from '@/lib/api';
import { MessagesMenu } from '@/components/MessagesMenu';
import { MesDemarchesCard } from '@/components/habitant/MesDemarchesCard';
import { MesInfosCard, AlerteEnCoursBanner } from '@/components/habitant/MesInfosCard';
import type { TenantDetail, UserRole, UserService, ScolaireEcole } from '@roronoa-collectivites/shared-types';
import {
  Hammer,
  Users,
  GraduationCap,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  UserCog,
  Bell,
  Vote,
  Landmark,
  BarChart3,
  UserRound,
  Calculator,
  Baby,
  ArrowRight,
  Radio,
  MapPin,
  Info,
  Phone,
  Mail,
  Globe,
  Boxes,
  Clock,
  Archive,
  CalendarClock,
} from 'lucide-react';

// ============================================================================
// Types tuile module (partagés entre vue pro et vue administré)
// ============================================================================

interface ModuleTile {
  key: string;
  label: string;
  description: string;
  icon: typeof Hammer;
  /** Couleur module — clé typée Module de @/components/ui. */
  color: 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'rh' | 'compta' | 'petite_enfance';
  phase: string;
  active: boolean;
  /** Cible de navigation si actif (pour vue pro : par rôle ; pour administré : direct). */
  hrefByRole?: Partial<Record<UserRole, string>>;
  /** Cible directe (administré). Prioritaire sur hrefByRole. */
  href?: string;
  /** L13.28 — service VPC auquel ce module appartient. */
  service?: UserService;
  /** L13.28 — route citoyenne du domaine, pour un agent hors de ce service. */
  citizenHref?: string;
  /** Clé de visibilité administré (pilotée par le maire dans Admin → Modules). */
  visKey?: string;
  metric?: string;
}

// ============================================================================
// Modules vue pro (super_admin, admin, manager, agent)
// ============================================================================

const PRO_MODULES: ModuleTile[] = [
  {
    key: 'tech',
    label: 'Service technique',
    description: 'Voirie · espaces verts · bâtiments',
    icon: Hammer,
    color: 'tech',
    phase: 'phase 2A.1',
    active: true,
    hrefByRole: {
      super_admin: '/service-technique/signalements',
      admin: '/service-technique/signalements',
      manager: '/service-technique/planning',
      agent: '/service-technique/ma-tournee',
      administre: '/mes-signalements', // safety net si fallback
    },
    service: 'service_technique',
    citizenHref: '/mes-signalements',
  },
  {
    key: 'grc',
    label: 'GRC, communication',
    description: 'Demandes · newsletter · alertes',
    icon: MessageSquare,
    color: 'grc',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/grc',
      admin: '/grc',
      manager: '/grc',
      // L13.13.1 — agent ré-autorisé en lecture seule (correction L13.11)
      agent: '/grc',
    },
    service: 'grc',
    citizenHref: '/mes-alertes',
    metric: 'canal unifié administrés',
  },
  {
    key: 'poi',
    label: 'Points d\'intérêt',
    description: 'Tourisme · entraide · sécurité',
    icon: MapPin,
    color: 'grc',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/grc/points-interet',
      admin: '/grc/points-interet',
      manager: '/grc/points-interet',
      agent: '/grc/points-interet',
    },
    service: 'grc',
    citizenHref: '/points-interet',
    metric: 'carte des points utiles',
  },
  {
    // Refonte UX (point Q1 tranché le 24/09) — les projets sont l'outil des
    // managers (rétroplannings), pas un réglage : tuile du lanceur. Le serveur les
    // ouvrait déjà aux managers (requireWriter) : aucun droit nouveau.
    key: 'projets',
    label: 'Projets',
    description: 'Rétroplannings · paliers · jour J',
    icon: CalendarClock,
    color: 'grc',
    phase: 'actif',
    active: true,
    hrefByRole: { super_admin: '/admin/projets', admin: '/admin/projets', manager: '/admin/projets' },
    metric: 'projets en cours',
  },
  {
    // Refonte UX, chantier 1 (D8) — les archives sont un outil de travail des
    // managers et des agents, pas un réglage : elles quittent Administration.
    key: 'archives',
    label: 'Archives',
    description: 'Inventaire · récolement · éliminations · sorties',
    icon: Archive,
    color: 'compta',
    phase: 'actif',
    active: true,
    hrefByRole: { super_admin: '/admin/archives', admin: '/admin/archives', manager: '/admin/archives', agent: '/admin/archives' },
    metric: 'armoires · dossiers',
  },
  {
    key: 'rh',
    label: 'Ressources humaines',
    description: 'Plannings · congés · heures sup · absences',
    icon: Clock,
    color: 'rh',
    phase: 'actif',
    active: true,
    // RH transverse : tous les profils internes vont sur /rh, la page adapte
    // son contenu au rôle (agent = ses demandes ; manager = son service ;
    // admin/maire = pilotage consolidé).
    hrefByRole: {
      super_admin: '/rh',
      admin: '/rh',
      manager: '/rh',
      agent: '/rh?vue=perso',
    },
    citizenHref: '/rh?vue=perso',
    metric: 'congés · heures · absences',
  },
  {
    key: 'asso',
    label: 'Association / Acteurs locaux',
    description: 'Annuaire · commerces & producteurs · subventions · salles',
    icon: Users,
    color: 'asso',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/associations',
      admin: '/associations',
      manager: '/associations',
      agent: '/associations',
    },
    service: 'associations',
    citizenHref: '/associations-annuaire',
    metric: 'Annuaire des associations',
  },
  {
    key: 'ecole',
    label: 'Scolaire / Cantine',
    description: 'Cantine · périscolaire · ALSH',
    icon: GraduationCap,
    color: 'cantine',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/scolaire',
      admin: '/scolaire',
      manager: '/scolaire',
      agent: '/scolaire/mon-planning',
    },
    service: 'scolaire_cantine',
    citizenHref: '/scolaire-cantine',
    metric: '~120 élèves attendus',
  },
  {
    key: 'comptable',
    label: 'Comptabilité',
    description: 'Encaissements · ventilation · dépôts',
    icon: Calculator,
    color: 'compta',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/comptabilite',
      admin: '/comptabilite',
      manager: '/comptabilite',
      agent: '/comptabilite',
    },
    service: 'comptable',
    metric: 'régie & flux financiers',
  },
  {
    key: 'urba',
    label: 'Urbanisme',
    description: 'Dépôt SVE · instruction · PLU & documents',
    icon: Building2,
    color: 'urba',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/urbanisme',
      admin: '/urbanisme',
      manager: '/urbanisme',
      agent: '/urbanisme',
    },
    service: 'urbanisme',
    citizenHref: '/mes-demandes-urbanisme',
    metric: 'CU · DP · PC · PA',
  },
  {
    key: 'petite_enfance',
    label: 'Petite enfance',
    description: 'Crèche · RAM · pré-inscriptions',
    icon: Baby,
    color: 'petite_enfance',
    phase: 'actif',
    active: true,
    hrefByRole: {
      super_admin: '/petite-enfance',
      admin: '/petite-enfance',
      manager: '/petite-enfance',
      agent: '/petite-enfance',
    },
    // Vue citoyenne de la brique = espace parent PE dédié. Un interne d'un AUTRE
    // service (ex. manager scolaire/cantine) doit atterrir ici en mode parent,
    // PAS sur /scolaire-cantine (qui est l'écran de gestion de SON service).
    citizenHref: '/petite-enfance/espace',
    service: 'petite_enfance',
    metric: 'accueil du jeune enfant',
  },
];

// ============================================================================
// Modules vue administré (citoyen) — Roadmap §4.1 validée 21 mai 2026
// ============================================================================

const ADMINISTRE_MODULES: ModuleTile[] = [
  {
    key: 'mes-signalements',
    label: 'Mes signalements techniques',
    description: 'Suivre et déclarer un incident sur la voie publique',
    icon: MapPin,
    color: 'tech',
    phase: 'actif',
    active: true,
    href: '/mes-signalements',
    visKey: 'mes_signalements',
  },
  {
    key: 'mes-alertes',
    label: 'Infos, alertes & services',
    description: 'Alertes · menus cantine · horaires · démarches · déchets · RDV · cimetière',
    icon: Bell,
    color: 'grc',
    phase: 'actif',
    active: true,
    href: '/mes-alertes',
    visKey: 'mes_alertes',
  },
  {
    key: 'points-interet',
    label: "Points d'intérêt",
    description: 'Tourisme · entraide · sécurité · carte des lieux utiles',
    icon: MapPin,
    color: 'grc',
    phase: 'actif',
    active: true,
    href: '/points-interet',
    service: 'grc',
    visKey: 'points_interet',
  },
  {
    key: 'louer-materiel',
    label: 'Louer du matériel / des salles',
    description: 'Réserver chapiteaux, tables, chaises, sono, salles…',
    icon: Boxes,
    color: 'tech',
    phase: 'actif',
    active: true,
    href: '/louer-materiel',
    visKey: 'location_salles_materiel',
  },
  {
    key: 'mes-referendums',
    label: 'Consultations',
    description: 'Référendums & consultations · donnez votre avis',
    icon: Vote,
    color: 'grc',
    phase: 'actif',
    active: true,
    href: '/mes-referendums',
    visKey: 'mes_alertes',
  },
  {
    key: 'mon-asso',
    label: 'Mes associations / Acteurs locaux',
    description: 'Annuaire · suivre & adhérer · me référencer (commerce, producteur…)',
    icon: Users,
    color: 'asso',
    phase: 'actif',
    active: true,
    href: '/mon-association',
    service: 'associations',
    visKey: 'associations',
  },
  {
    key: 'enfance',
    label: 'Enfance',
    description: 'Scolaire · cantine · périscolaire · ALSH · petite enfance',
    icon: GraduationCap,
    color: 'cantine',
    phase: 'actif',
    active: true,
    href: '/scolaire-cantine',
    visKey: 'enfance',
    metric: 'famille, pièces & services',
  },
  {
    key: 'mes-urba',
    label: "Mes demandes d'urbanisme",
    description: 'Permis · DP · certificat d\'urbanisme',
    icon: Building2,
    color: 'urba',
    phase: 'actif',
    active: true,
    href: '/mes-demandes-urbanisme',
    service: 'urbanisme',
    visKey: 'urbanisme',
    metric: 'À venir',
  },
];

const today = new Date().toLocaleDateString('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

// ============================================================================
// Composant racine : router par rôle
// ============================================================================

// ============================================================================
// L13.15.1 — Entrée du dashboard jouée UNE SEULE FOIS par session.
//
// Symptôme corrigé : à chaque retour sur le dashboard, les tuiles rejouaient
// leur animation d'apparition décalée (« escalier ») — sensation de
// clignotement / de page pas fluide. Le décalage par tuile (animationDelay)
// est supprimé : le dashboard apparaît d'un bloc, et seulement à la première
// arrivée de la session (instantané et calme aux retours suivants).
//
// Le flag vit au niveau du module : il persiste entre les démontages /
// remontages du composant (navigation), et n'est réinitialisé qu'au
// rechargement complet de la page.
// ============================================================================
let dashboardEntrancePlayed = false;

/** true uniquement au tout premier rendu du dashboard de la session. */
function useDashboardEntrance(): boolean {
  // Lazy initializer : lit le flag au 1er rendu seulement.
  const [animate] = useState(() => !dashboardEntrancePlayed);
  useEffect(() => {
    dashboardEntrancePlayed = true;
  }, []);
  return animate;
}

/** Libellés du type d'établissement pour l'affichage administré. */
const TYPE_ETAB_LABEL: Record<string, string> = {
  maternelle: 'École maternelle',
  elementaire: 'École élémentaire',
  primaire: 'École primaire',
};
function plageHoraire(a: string | null | undefined, b: string | null | undefined): string | null {
  if (a && b) return `${a} – ${b}`;
  if (a) return `à partir de ${a}`;
  if (b) return `jusqu'à ${b}`;
  return null;
}
/** Construit les lignes d'horaires (école / garderie / ALSH) d'un établissement. */
function lignesHorairesEtab(e: ScolaireEcole): string[] {
  const out: string[] = [];
  const h = e.horaires;
  if (h && (h.ouverture || h.finJournee)) {
    const matin = plageHoraire(h.ouverture, h.finMatin);
    const aprem = plageHoraire(h.repriseApresMidi, h.finJournee);
    const merc = plageHoraire(h.mercrediOuverture, h.mercrediFin);
    const parts = [matin && `matin ${matin}`, aprem && `après-midi ${aprem}`].filter(Boolean).join(' · ');
    if (parts) out.push(`École : ${parts}`);
    if (merc) out.push(`École (mercredi) : ${merc}`);
  }
  const g = e.garderie;
  if (g) {
    const p = plageHoraire(g.ouvertureMatin, g.fermetureSoir);
    const merc = plageHoraire(g.mercrediOuverture, g.mercrediFermeture);
    if (p) out.push(`Garderie : ${p}`);
    if (merc) out.push(`Garderie (mercredi) : ${merc}`);
  }
  const a = e.alsh;
  if (a) {
    const p = plageHoraire(a.ouverture, a.fermeture);
    const merc = plageHoraire(a.mercrediOuverture, a.mercrediFermeture);
    if (p) out.push(`ALSH : ${p}`);
    if (merc) out.push(`ALSH (mercredi) : ${merc}`);
  }
  return out;
}

export function DashboardPage() {
  const { user } = useAuth();
  // Pendant le loading auth, on rend rien (RequireAuth fait déjà un spinner global)
  if (!user) return null;

  if (user.role === 'administre') {
    return <DashboardAdministre />;
  }
  return <DashboardPro />;
}

// ============================================================================
// Topbar mutualisée — réutilisée par les 2 vues
// ============================================================================

function DashboardTopbar() {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const userInitials = user?.prenom?.[0]?.toUpperCase() ?? user?.nom?.[0]?.toUpperCase() ?? '?';

  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
      style={{
        backgroundImage:
          'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
          'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
      }}
    >
      <LogoMark icon={Landmark} accent="tech" size="md" aria-label="Mairie" />
      <div className="min-w-0">
        <SectionEyebrow>Mairie de</SectionEyebrow>
        <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
          {tenant?.nom ?? '—'}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Cloche notifications dynamique — L13.6 (compteur par rôle + navigation contextuelle) */}
        {/* Refonte UX (C1) — « Messages » remplace la cloche : notifications, messagerie, diffusions. */}
        <MessagesMenu />

        {/* User pill : avatar + nom + role, masqué sur mobile */}
        {user && (
          <span className="hidden sm:inline-flex items-center gap-2 rounded-r-pill border border-border bg-surface/60 pl-1 pr-3 py-1 text-xs backdrop-blur-glass">
            <Avatar initials={userInitials} accent="tech" size="xs" />
            <span className="text-ink font-medium">
              {user.prenom} {user.nom}
            </span>
            <span className="text-ink-subtle">·</span>
            <span className="text-ink-subtle">{user.role}</span>
          </span>
        )}

        {/* Actions account / 2fa / logout — petits boutons icon-only */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => navigate('/compte')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-r-lg text-ink-muted transition-all hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label="Mon compte"
            title="Mon compte"
          >
            <UserCog className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => navigate('/2fa')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-r-lg text-ink-muted transition-all hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label="Authentification à deux facteurs"
            title="2FA"
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => void logout().then(() => navigate('/connexion'))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-r-lg text-ink-muted transition-all hover:bg-surface/60 hover:text-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label="Déconnexion"
            title="Déconnexion"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
}

// ============================================================================
// VUE PROS — super_admin, admin, manager, agent (inchangée vs R4)
// ============================================================================

function DashboardPro() {
  const navigate = useNavigate();
  const entranceAnim = useDashboardEntrance();
  const { user } = useAuth();
  const { isModuleActif } = useModules();

  // Métrique dynamique pour la tuile Service technique (Phase 2A.1)
  const [techMetric, setTechMetric] = useState<string>('—');

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const result = await api.serviceTechnique.signalements.list({
          statut: ['recu', 'planifie', 'en_cours'],
          pageSize: 1,
        });
        setTechMetric(`${result.total} fiche${result.total !== 1 ? 's' : ''} en cours`);
      } catch {
        setTechMetric('—');
      }
    })();
  }, [user]);

  const greeting = user?.prenom ? `Bonjour ${user.prenom}` : null;

  return (
    <div className="min-h-screen text-ink">
      <DashboardTopbar />

      <main
        id="main-content"
        className={`mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10 ${entranceAnim ? 'animate-fade-in' : ''}`}
      >
        {/* Hero */}
        <SectionEyebrow as="div" className="capitalize">{today}</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
          {greeting ?? 'Quel service souhaitez-vous consulter aujourd\'hui\u00a0?'}
        </h1>
        {greeting && (
          <p className="mt-2 text-sm text-ink-muted">
            Quel service souhaitez-vous consulter aujourd'hui&nbsp;?
          </p>
        )}

        {/* GRID 6 TUILES MODULES PRO */}
        <div className="mt-3 grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRO_MODULES.filter((mod) => isModuleActif(mod.service)).map((mod) => {
            const effectiveMetric = mod.key === 'tech' ? techMetric : mod.metric;
            // L13.28 — cloisonnement : on ne masque jamais la tuile, mais si
            // l'agent n'est pas du service du module, elle l'emmène vers la
            // vue citoyenne du domaine (comme un administré), pas la vue métier.
            // Maires/adjoints et super_admin gardent l'accès métier partout.
            const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
            // Cumul : le service principal OU un service additionnel coché ouvre
            // la vue métier de la brique (pas seulement le service principal).
            const userServices: string[] = [
              ...(user?.service ? [user.service] : []),
              ...(user?.servicesAdditionnels ?? []),
            ];
            // Recette du 28/09 : une tuile TRANSVERSE (Archives, Projets : aucun service)
            // s'ouvre pour les rôles qui ont un lien, et disparaît pour les autres
            // (elle s'affichait sans être cliquable pour un manager d'un autre service).
            const transverse = !mod.service;
            if (transverse && !(user && mod.hrefByRole?.[user.role])) return null;
            const inService = isAdmin || transverse || (!!mod.service && userServices.includes(mod.service));
            // RH : la vue métier « gestion » est réservée à l'admin/maire et au
            // MANAGER DU SERVICE RH (principal ou cumulé) — pas à tous les managers.
            // Les autres internes (manager d'un autre service, agent) n'ont que
            // leur vue personnelle RH.
            const isRhModule = mod.key === 'rh';
            // Demande Henrico 20/08 : pour un AGENT, la tuile RH disparaît —
            // « Mon dossier personnel » porte déjà tous ses raccourcis (congés,
            // planning, notes de frais, dossier) et deux tuiles RH côte à côte
            // brouillaient la lecture. La route /rh?vue=perso reste accessible
            // par ces raccourcis ; seuls manager/admin gardent la tuile,
            // c'est leur porte vers la gestion.
            if (isRhModule && user?.role === 'agent') return null;
            const aServiceRh = userServices.includes('rh');
            const rhGestion = isRhModule && (
              user?.role === 'admin' || user?.role === 'super_admin' ||
              (user?.role === 'manager' && aServiceRh)
            );
            const href = mod.active && user
              ? (isRhModule
                  ? (rhGestion ? '/rh' : '/rh?vue=perso')
                  : (inService ? mod.hrefByRole?.[user.role] : mod.citizenHref))
              : undefined;
            // Vue ADMINISTRÉE de SA propre brique, pour agir à titre personnel
            // (s'inscrire à la cantine, faire un signalement…). Proposée à tous
            // les internes — y compris maire/adjoint/admin. Affichée dès qu'une
            // route citoyenne existe (même si la brique métier n'est pas encore
            // active, ex. Urbanisme phase 5 : l'administré y aura droit), sauf si
            // la vue pro EST déjà la vue citoyenne.
            // RH : plus de lien « Ma vue personnelle » sous la tuile (retour
            // mairie du 28/07). Depuis la vague A, « Mon dossier personnel » porte
            // ces raccourcis — congés, planning, notes de frais, dossier — et
            // deux portes vers le même endroit brouillaient la lecture.
            const showCitizenSwitch = isRhModule
              ? false
              : (!!user && inService && !!mod.service &&
                 !!mod.citizenHref && mod.citizenHref !== href);
            return (
              <ModuleTileCard
                key={mod.key}
                mod={mod}
                metric={effectiveMetric}
                onClick={href ? () => navigate(href) : undefined}
                citizenHref={showCitizenSwitch ? mod.citizenHref : undefined}
                onCitizenClick={showCitizenSwitch ? () => navigate(mod.citizenHref!) : undefined}
                citizenLabel="Ouvrir mon espace habitant"
                citizenLabelShort="Mon espace habitant"
                reserveLinkSpace
              />
            );
          })}

          {/* Consultations citoyennes — tuile PERMANENTE pour tout le personnel.
              Un employé municipal reste un habitant : il peut proposer des idées
              (boîte à idées) et voter aux consultations, à tout moment. Même
              destination que la tuile des administrés : /mes-referendums. */}
          <ModuleTileCard
            mod={{
              key: 'mes-referendums-pro',
              label: 'Consultations',
              description: 'Référendums, consultations & boîte à idées · votre avis',
              icon: Vote,
              color: 'grc',
              phase: 'actif',
              active: true,
              href: '/mes-referendums',
              service: 'grc',
            }}
            metric={undefined}
            onClick={() => navigate('/mes-referendums')}
            reserveLinkSpace
          />

          {/* Mon dossier personnel (mig 253) — tuile TRANSVERSE, comme la RH : pas de
              `service`, donc pas de test d'appartenance. C'est tout le sens de la
              brique — un agent des espaces verts et un agent d'accueil y entrent
              pareil. Elle disparaît si la commune désactive le module `agents`. */}
          {isModuleActif('agents') && (
            <ModuleTileCard
              mod={{
                key: 'mon-espace-agent',
                label: 'Mon dossier personnel',
                description: 'Liens utiles · numéros d\'urgence · raccourcis',
                icon: UserRound,
                color: 'rh',
                phase: 'actif',
                active: true,
                href: '/mon-espace',
              }}
              metric={undefined}
              onClick={() => navigate('/mon-espace')}
              reserveLinkSpace
            />
          )}
        </div>

        {user && (user.role === 'super_admin' || user.role === 'admin') && (
          <div className="mt-6">
            <Card variant="glass" padded className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-r-lg bg-gradient-tech text-white shadow-glow-tech flex-shrink-0"
                  aria-hidden="true"
                >
                  <BarChart3 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink">Vue consolidée</div>
                  <div className="text-xs text-ink-subtle truncate">
                    KPIs de tous les services activés · idéal pour conseil municipal
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/dashboard/maire')}
                className="inline-flex h-10 items-center gap-2 rounded-r-lg bg-gradient-tech text-white shadow-glow-tech border border-tech-300/40 px-4 text-sm font-medium flex-shrink-0 transition-transform hover:scale-[1.02]"
                aria-label="Ouvrir la vue maire consolidée"
              >
                Ouvrir
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </Card>
          </div>
        )}

        {/* Messagerie interne — outil transverse, accessible à tout le personnel */}
        <button
          type="button"
          onClick={() => navigate('/grc/messagerie')}
          className="mt-3 w-full text-left rounded-r-2xl border border-border bg-surface/55 backdrop-blur-glass shadow-glass transition-all hover:border-grc/30 hover:shadow-glass-hi hover:-translate-y-0.5 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg p-5"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <LogoMark icon={MessageSquare} accent="grc" size="md" />
              <div className="min-w-0">
                <div className="font-medium text-ink">Messagerie interne</div>
                <div className="text-xs text-ink-subtle truncate">
                  Échanges entre agents et services · tous services
                </div>
              </div>
            </div>
            <span
              className="inline-flex h-10 w-10 items-center justify-center rounded-r-lg bg-grc-tint/80 text-grc-fg flex-shrink-0"
              aria-hidden="true"
            >
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </button>

        {/* Tuile Administration (super_admin / admin) */}
        {user && (user.role === 'super_admin' || user.role === 'admin') && (
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="mt-3 w-full text-left rounded-r-2xl border border-border bg-surface/55 backdrop-blur-glass shadow-glass transition-all hover:border-tech/30 hover:shadow-glass-hi hover:-translate-y-0.5 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <LogoMark icon={ShieldCheck} accent="tech" size="md" />
                <div className="min-w-0">
                  <div className="font-medium text-ink">Administration</div>
                  <div className="text-xs text-ink-subtle truncate">
                    Utilisateurs · permissions · audit log
                  </div>
                </div>
              </div>
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-r-lg bg-tech-tint/80 text-tech-fg flex-shrink-0"
                aria-hidden="true"
              >
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </button>
        )}

        {/* Tuile Plateforme (isPlatformAdmin) */}
        {/* Le type User shared-types ne mentionne pas isPlatformAdmin (cf. dette projet) ;
            on cast localement pour préserver la check sans casser le build. */}
        {user && (user as { isPlatformAdmin?: boolean }).isPlatformAdmin && (
          <button
            type="button"
            onClick={() => navigate('/platform')}
            className="mt-3 w-full text-left rounded-r-2xl border border-urgent/30 backdrop-blur-glass shadow-glass transition-all hover:shadow-glow-urgent hover:-translate-y-0.5 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg p-5"
            style={{
              backgroundImage:
                'radial-gradient(180px 90px at 80% 0%, oklch(var(--c-urgent) / 0.28), transparent 70%), ' +
                'linear-gradient(180deg, oklch(var(--c-surface) / 0.6), oklch(var(--c-bg-2) / 0.55))',
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-to-b from-urgent to-urgent/70 text-white shadow-glow-urgent flex-shrink-0"
                  style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-urgent) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
                  aria-hidden="true"
                >
                  <Radio className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink">Plateforme One For All Distribution</div>
                  <div className="text-xs text-ink-subtle truncate">
                    Supervision cross-tenant · hotline support à distance
                  </div>
                </div>
              </div>
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-r-lg bg-urgent-tint/70 text-urgent flex-shrink-0"
                aria-hidden="true"
              >
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </button>
        )}
      </main>

      {/* Footer discret */}
      <footer
        className="border-t border-border px-4 py-3 text-center text-2xs text-ink-dim backdrop-blur-glass mt-2"
        style={{
          backgroundImage: 'linear-gradient(180deg, transparent, oklch(var(--c-bg-2) / 0.3))',
        }}
      >
        VPC · édité par <span className="text-ink-subtle">One For All Distribution</span>
        {' · '}hébergement souverain UE{' · '}RGPD conforme
      </footer>
    </div>
  );
}

// ============================================================================
// VUE ADMINISTRÉ — 5 tuiles citoyen (L13.5)
// ============================================================================

function DashboardAdministre() {
  const navigate = useNavigate();
  const entranceAnim = useDashboardEntrance();
  const { user, tenant } = useAuth();
  const { isModuleActif, isVisibleAdministre } = useModules();

  // Métrique dynamique pour Mes signalements (compteur des signalements de l'administré)
  const [mesSignalementsCount, setMesSignalementsCount] = useState<string>('—');

  // L13.7 — charge le détail tenant pour afficher les coordonnées mairie
  // L'endpoint /api/me/tenant est accessible à tout user authentifié (administré inclus).
  const [tenantDetail, setTenantDetail] = useState<TenantDetail | null>(null);

  // Horaires des établissements (école / garderie / ALSH), affichés à l'administré.
  // /api/scolaire/etablissements-horaires est accessible à tout user authentifié.
  const [etablissements, setEtablissements] = useState<ScolaireEcole[]>([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const result = await api.serviceTechnique.signalements.listMine({ pageSize: 1 });
        setMesSignalementsCount(
          `${result.total} signalement${result.total !== 1 ? 's' : ''}`
        );
      } catch {
        setMesSignalementsCount('—');
      }
    })();
  }, [user]);

  // L13.7 — charge silencieusement les coordonnées mairie. Si l'endpoint refuse
  // l'accès (403) ou échoue, l'encart "Coordonnées mairie" n'est juste pas affiché —
  // pas de plantage, pas d'erreur visuelle. C'est un nice-to-have, pas un must.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.admin.tenant.get();
        if (!cancelled) setTenantDetail(detail);
      } catch {
        // Silencieux : pas d'encart si pas de détail dispo
        if (!cancelled) setTenantDetail(null);
      }
      try {
        const etabs = await api.scolaire.listEtablissementsHoraires();
        if (!cancelled) setEtablissements(etabs);
      } catch {
        // Silencieux : pas d'encart horaires si l'accès échoue
        if (!cancelled) setEtablissements([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const greeting = user?.prenom ? `Bonjour ${user.prenom}` : null;

  // Test si la mairie a au moins une coordonnée renseignée → afficher l'encart
  const hasContactInfo = !!(
    tenantDetail &&
    (tenantDetail.telephone || tenantDetail.emailContact || tenantDetail.adresse)
  );

  // Établissements ayant au moins une plage horaire renseignée.
  const etablissementsAvecHoraires = etablissements
    .map((e) => ({ etab: e, lignes: lignesHorairesEtab(e) }))
    .filter((x) => x.lignes.length > 0);

  return (
    <div className="min-h-screen text-ink">
      <DashboardTopbar />

      <main
        id="main-content"
        className={`mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10 ${entranceAnim ? 'animate-fade-in' : ''}`}
      >
        {/* Hero administré */}
        <SectionEyebrow as="div" className="capitalize">{today}</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
          {greeting ?? 'Bienvenue dans votre espace citoyen'}
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          Que souhaitez-vous faire aujourd'hui&nbsp;?
        </p>

        {/* Chantier 7 (maquette 18, retenue le 24/09) — l'accueil de suivi :
            alerte en cours, « Ce que je suis », « La mairie vous informe »,
            puis « Mes services » (toutes les tuiles, conservées). */}
        <AlerteEnCoursBanner />
        <MesDemarchesCard />
        <MesInfosCard />
        <h2 className="mt-6 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">Mes services</h2>

        {/* GRID 5 TUILES ADMINISTRÉ */}
        <div className="mt-8 grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ADMINISTRE_MODULES.filter((mod) => {
            // Masquage piloté par le maire (visibilité administré).
            if (mod.visKey && !isVisibleAdministre(mod.visKey)) return false;
            // « Enfance » : visible si AU MOINS un des deux modules internes tourne.
            if (mod.key === 'enfance') return isModuleActif('scolaire_cantine') || isModuleActif('petite_enfance');
            // Autres : si la brique est adossée à un module interne, il doit être actif.
            return isModuleActif(mod.service);
          }).map((mod) => {
            const effectiveMetric = mod.key === 'mes-signalements' ? mesSignalementsCount : mod.metric;
            const href = mod.href; // tuile administré utilise href direct
            // Les tuiles non-actives mènent quand même à un placeholder élégant
            // (pas de "cul-de-sac" UX). C'est la décision validée.
            return (
              <ModuleTileCard
                key={mod.key}
                mod={mod}
                metric={effectiveMetric}
                onClick={href ? () => navigate(href) : undefined}
              />
            );
          })}
        </div>

        {/* Encart "Vous êtes connecté en tant que citoyen" — remplace le bandeau Phase 1 */}
        <Card variant="glass" padded className="mt-8">
          <div className="flex items-start gap-3">
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg"
              aria-hidden="true"
            >
              <Info className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-ink">
                Vous êtes connecté·e en tant que citoyen·ne de {tenant?.nom ?? 'votre commune'}
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                Vous pouvez à tout moment modifier vos préférences de communication, exporter vos
                données ou supprimer votre compte depuis votre espace personnel.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => navigate('/compte')}
                  className="inline-flex items-center gap-1.5 rounded-r-pill border border-border bg-surface/60 px-3 py-1.5 text-xs font-medium text-ink-muted backdrop-blur-glass hover:bg-surface/80 hover:text-ink transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                >
                  <UserCog className="h-3.5 w-3.5" aria-hidden="true" />
                  Mon compte
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* L13.7 — Encart "Coordonnées de la mairie" — visible UNIQUEMENT si la
            mairie a renseigné au moins une coordonnée (adresse, tél, email) via
            /admin/parametres-mairie. Sinon, l'encart est silencieusement masqué. */}
        {hasContactInfo && tenantDetail && (
          <Card variant="glass" padded className="mt-4">
            <div className="flex items-start gap-3">
              <LogoMark icon={Landmark} accent="tech" size="md" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink">Coordonnées de la mairie</div>
                <p className="mt-1 text-sm text-ink-muted">
                  Pour toute question administrative qui ne peut pas attendre l'ouverture des
                  services en ligne, contactez directement votre mairie.
                </p>

                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  {(tenantDetail.adresse || tenantDetail.ville) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-tech" aria-hidden="true" />
                      <div className="min-w-0">
                        <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Adresse</dt>
                        <dd className="mt-0.5 text-ink">
                          {tenantDetail.adresse && <div>{tenantDetail.adresse}</div>}
                          {(tenantDetail.codePostal || tenantDetail.ville) && (
                            <div className="text-ink-muted">
                              {[tenantDetail.codePostal, tenantDetail.ville].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </dd>
                      </div>
                    </div>
                  )}

                  {tenantDetail.telephone && (
                    <div className="flex items-start gap-2">
                      <Phone className="mt-0.5 h-4 w-4 flex-shrink-0 text-tech" aria-hidden="true" />
                      <div className="min-w-0">
                        <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Téléphone</dt>
                        <dd className="mt-0.5">
                          {/* L13.9 fix Z5 — PhoneLink détecte mobile vs desktop :
                              mobile → href=tel: direct (ouvre dialer natif iOS/Android)
                              desktop → bouton qui ouvre Dialog R13 interne (PAS Chrome modale) */}
                          <PhoneLink number={tenantDetail.telephone} />
                        </dd>
                      </div>
                    </div>
                  )}

                  {tenantDetail.emailContact && (
                    <div className="flex items-start gap-2">
                      <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-tech" aria-hidden="true" />
                      <div className="min-w-0">
                        <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Email</dt>
                        <dd className="mt-0.5 truncate">
                          <a
                            href={`mailto:${tenantDetail.emailContact}`}
                            className="text-ink hover:text-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm"
                          >
                            {tenantDetail.emailContact}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}

                  {tenantDetail.siret && (
                    <div className="flex items-start gap-2">
                      <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-tech" aria-hidden="true" />
                      <div className="min-w-0">
                        <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">SIRET</dt>
                        <dd className="mt-0.5 font-mono text-xs text-ink-muted">{tenantDetail.siret}</dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </Card>
        )}

        {/* Encart "Horaires des établissements" — école / garderie / ALSH.
            Visible uniquement si au moins un établissement a des horaires saisis
            (via /admin → config scolaire). Sinon silencieusement masqué. */}
        {etablissementsAvecHoraires.length > 0 && (
          <Card variant="glass" padded className="mt-4">
            <div className="flex items-start gap-3">
              <LogoMark icon={Clock} accent="cantine" size="md" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink">Horaires des établissements</div>
                <p className="mt-1 text-sm text-ink-muted">
                  Horaires d'accueil de l'école, de la garderie et du centre de loisirs (ALSH).
                </p>

                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {etablissementsAvecHoraires.map(({ etab, lignes }) => (
                    <div key={etab.id} className="flex items-start gap-2">
                      <GraduationCap className="mt-0.5 h-4 w-4 flex-shrink-0 text-cantine" aria-hidden="true" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-ink">{etab.nom}</div>
                        {TYPE_ETAB_LABEL[etab.type] && (
                          <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                            {TYPE_ETAB_LABEL[etab.type]}
                          </div>
                        )}
                        <ul className="mt-1 space-y-0.5 text-sm text-ink-muted">
                          {lignes.map((l, i) => (
                            <li key={i}>{l}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </main>

      {/* Footer discret */}
      <footer
        className="border-t border-border px-4 py-3 text-center text-2xs text-ink-dim backdrop-blur-glass mt-2"
        style={{
          backgroundImage: 'linear-gradient(180deg, transparent, oklch(var(--c-bg-2) / 0.3))',
        }}
      >
        VPC · édité par <span className="text-ink-subtle">One For All Distribution</span>
        {' · '}hébergement souverain UE{' · '}RGPD conforme
      </footer>
    </div>
  );
}

// ============================================================================
// Composant tuile module — Card accent avec halo radial + LogoMark + footer
// (utilisé par les 2 vues, comportement inchangé vs R4)
// ============================================================================

function ModuleTileCard({
  mod,
  metric,
  onClick,
  citizenHref,
  onCitizenClick,
  citizenLabel = "Ouvrir mon espace habitant",
  citizenLabelShort,
  reserveLinkSpace = false,
}: {
  mod: ModuleTile;
  metric: string | undefined;
  onClick?: () => void;
  citizenHref?: string;
  onCitizenClick?: () => void;
  citizenLabel?: string;
  /** Libellé court affiché sur mobile (2 colonnes) ; défaut = citizenLabel. */
  citizenLabelShort?: string;
  /** Vue maire : réserve la hauteur du lien « administré » même absent, pour que
   *  toutes les tuiles de la rangée aient la même hauteur de carré coloré. */
  reserveLinkSpace?: boolean;
}) {
  const Icon = mod.icon;
  const interactive = !!onClick;

  const dotBg: Record<ModuleTile['color'], string> = {
    tech:    'bg-tech',
    asso:    'bg-asso',
    ecole:   'bg-ecole',
    urba:    'bg-urba',
    grc:     'bg-grc',
    cantine: 'bg-cantine',
    rh:      'bg-rh',
    compta:  'bg-compta',
    petite_enfance: 'bg-petite_enfance',
  };
  const focusRing: Record<ModuleTile['color'], string> = {
    tech:    'focus-visible:ring-tech',
    asso:    'focus-visible:ring-asso',
    ecole:   'focus-visible:ring-ecole',
    urba:    'focus-visible:ring-urba',
    grc:     'focus-visible:ring-grc',
    cantine: 'focus-visible:ring-cantine',
    rh:      'focus-visible:ring-rh',
    compta:  'focus-visible:ring-compta',
    petite_enfance: 'focus-visible:ring-petite_enfance',
  };
  const dotBgClass = dotBg[mod.color];

  const Component: 'button' | 'div' = interactive ? 'button' : 'div';

  return (
    <div className="flex h-full flex-col">
    <Component
      {...(interactive
        ? {
            type: 'button' as const,
            onClick,
          }
        : {})}
      className={[
        'relative flex w-full flex-1 flex-col text-left rounded-r-2xl border backdrop-blur-glass p-3 sm:p-4',
        'min-h-[118px] sm:min-h-[168px]',
        'transition-all duration-200 ease-out-expo',
        interactive
          ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
          : 'opacity-90',
        interactive ? focusRing[mod.color] : '',
      ].join(' ')}
      style={{
        backgroundImage:
          `radial-gradient(180px 100px at 80% 0%, oklch(var(--c-${mod.color}) / 0.25), transparent 70%), ` +
          `linear-gradient(180deg, oklch(var(--c-surface) / 0.6), oklch(var(--c-bg-2) / 0.55))`,
        borderColor: `oklch(var(--c-${mod.color}) / 0.40)`,
        boxShadow:
          `0 0 0 1px oklch(var(--c-${mod.color}) / 0.30), 0 14px 40px -12px oklch(var(--c-${mod.color}) / 0.28)`,
      }}
      aria-label={interactive ? `Ouvrir ${mod.label}` : `${mod.label} (à venir, ${mod.phase})`}
    >
      {/* Top : LogoMark à gauche + Badge phase à droite */}
      <div className="flex items-start justify-between gap-2">
        <LogoMark icon={Icon} accent={mod.color} size="md" className="h-8 w-8 sm:h-9 sm:w-9" />
        {mod.active ? (
          <Badge variant="success" size="sm">actif</Badge>
        ) : (
          <Badge variant="neutral" size="sm" className="font-mono uppercase tracking-r-eyebrow">
            {mod.phase}
          </Badge>
        )}
      </div>

      {/* Milieu : titre + description */}
      <div className="mt-3 sm:mt-5">
        <h2
          className="font-display text-sm sm:text-lg font-semibold tracking-r-snug leading-tight"
          style={{ color: `oklch(var(--c-${mod.color}-fg))` }}
        >
          {mod.label}
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-ink-muted line-clamp-2">{mod.description}</p>
      </div>

      {/* Footer : dot + metric */}
      {metric && (
        <div className="mt-2.5 sm:mt-4 flex items-center gap-2 text-xs text-ink-subtle">
          <span
            className={`inline-block h-2 w-2 rounded-full ${dotBgClass}`}
            aria-hidden="true"
          />
          <span className="line-clamp-1">{metric}</span>
        </div>
      )}
    </Component>
    {/* Basculeur contextuel vers l'espace habitant de SA propre brique
        (agir à titre personnel). Option (a) actée le 27/07 : pas d'interrupteur
        global — la règle L13.28 envoie déjà l'agent en vue habitant sur les
        briques qui ne sont pas la sienne. */}
    {citizenHref && onCitizenClick ? (
      <button
        type="button"
        onClick={onCitizenClick}
        className="mt-1.5 inline-flex items-center gap-1 self-start rounded-r-md px-1.5 sm:px-2 py-1 text-[11px] sm:text-xs font-medium text-ink-subtle transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        aria-label={citizenLabel}
      >
        <UserRound className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {/* Libellé complet dès sm ; version courte sur mobile pour tenir en 2 colonnes. */}
        <span className="hidden sm:inline">{citizenLabel}</span>
        <span className="sm:hidden">{citizenLabelShort ?? citizenLabel}</span>
      </button>
    ) : reserveLinkSpace ? (
      // Pas de lien pour cette brique (ex. RH, Comptabilité) : on réserve la même
      // hauteur que le lien afin que le carré coloré reste aligné avec les autres.
      <span className="mt-1.5 px-2 py-1 text-[11px] sm:text-xs" aria-hidden="true">&nbsp;</span>
    ) : null}
    </div>
  );
}
